import React from 'react';

import { Checkbox, Heading, Label, LabeledControl, Paragraph, Spacer, Switch } from '@zillow/constellation';
import { HR } from '@zillow/zrs-comet';

import type { Section } from '../../../models';
import { SECURITY_DEPOSIT } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function SecurityDepositAdditionalOptions({ section, setSectionValues }: Props) {
  function onNoTenUnitsOrMore(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.TEN_UNITS_OR_MORE]: false,
    });
  }

  function onYesTenUnitsOrMore(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.TEN_UNITS_OR_MORE]: true,
    });
  }

  function onSelectRentalSecurityInsurance(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.RENTAL_SECURITY_INSURANCE]: !section.getFieldValue(
        SECURITY_DEPOSIT.FIELD_NAME.RENTAL_SECURITY_INSURANCE,
      ),
    });
  }

  function onSelectMonthlyPaymentPlan(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.MONTHLY_PAYMENT_PLAN]: !section.getFieldValue(
        SECURITY_DEPOSIT.FIELD_NAME.MONTHLY_PAYMENT_PLAN,
      ),
    });
  }

  function onSelectReducedSecurityDeposit(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.REDUCED_SECURITY_DEPOSIT]: !section.getFieldValue(
        SECURITY_DEPOSIT.FIELD_NAME.REDUCED_SECURITY_DEPOSIT,
      ),
    });
  }

  return (
    <>
      <Heading level={6} marginBottom="xs">
        Do you own and/or control more than 10 rental units?
      </Heading>
      <YesAndNoExpansion
        yesHandler={onYesTenUnitsOrMore}
        noHandler={onNoTenUnitsOrMore}
        yesActive={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.TEN_UNITS_OR_MORE)}
      >
        <Spacer marginTop="sm">
          <Label>In lieu of a security deposit, which option(s) will you allow for the property?</Label>
          <LabeledControl
            labelPosition="left"
            fluid
            size="lg"
            label={
              <Label>
                <Paragraph fontType="h6">Rental security insurance</Paragraph>
                <Paragraph>Criteria here tbd</Paragraph>
              </Label>
            }
            control={
              <Switch
                checkbox={
                  <Checkbox
                    onChange={onSelectRentalSecurityInsurance}
                    checked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.RENTAL_SECURITY_INSURANCE)}
                  />
                }
              />
            }
          />
          <HR />
          <LabeledControl
            labelPosition="left"
            fluid
            size="lg"
            label={
              <Label>
                <Paragraph fontType="h6">Monthly payments of security deposit</Paragraph>
                <Paragraph>Series of no less of 6 monthly equal payment installment plan</Paragraph>
              </Label>
            }
            control={
              <Switch
                checkbox={
                  <Checkbox
                    onChange={onSelectMonthlyPaymentPlan}
                    checked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.MONTHLY_PAYMENT_PLAN)}
                  />
                }
              />
            }
          />
          <HR />
          <LabeledControl
            labelPosition="left"
            fluid
            size="lg"
            label={
              <Label>
                <Paragraph fontType="h6">Reduced security deposit</Paragraph>
                <Paragraph>Shall be no more than 50% of the base rent of the unit</Paragraph>
              </Label>
            }
            control={
              <Switch
                checkbox={
                  <Checkbox
                    onChange={onSelectReducedSecurityDeposit}
                    checked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.REDUCED_SECURITY_DEPOSIT)}
                  />
                }
              />
            }
          />
        </Spacer>
      </YesAndNoExpansion>
    </>
  );
}

SecurityDepositAdditionalOptions.description =
  'Atlanta screen where security deposit options are based on whether or not the landlord owns more than 10 properties.';

export default SecurityDepositAdditionalOptions;
